// Shopify App Bridge v4 utilities for embedded app functionality
let appInstance = null;
let sessionTokenCache = null;
let sessionTokenExpiry = null;

/**
 * Normalize shop domain to standard format
 */
const normalizeShopDomain = (shop) => {
  if (!shop) return '';
  return shop.replace(/^https?:\/\//, '').split('/')[0];
};

/**
 * Get URL parameters for embedded app detection
 */
const getEmbeddedParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    shop: urlParams.get('shop'),
    host: urlParams.get('host'),
    embedded: urlParams.get('embedded')
  };
};

/**
 * Check if App Bridge is available in the current context
 */
export const isAppBridgeAvailable = () => {
  return typeof window !== 'undefined' &&
    !!(window.shopify && (window.shopify.createApp || window.shopify.app));
};

/**
 * Wait for the App Bridge to be available (loaded from HTML head)
 * App Bridge script is included in index.html with data-api-key
 */
const waitForAppBridge = () => {
  return new Promise((resolve, reject) => {
    console.log('[AppBridge] Waiting for window.shopify from HTML script...');

    if (window.shopify) {
      console.log('[AppBridge] ✅ window.shopify already available!');
      resolve(window.shopify);
      return;
    }

    // Wait for window.shopify to appear (max 5 seconds)
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds

    const checkShopify = () => {
      attempts++;
      if (window.shopify) {
        console.log(`[AppBridge] ✅ window.shopify loaded after ${attempts * 100}ms!`);
        resolve(window.shopify);
      } else if (attempts >= maxAttempts) {
        console.error('[AppBridge] ❌ Timeout waiting for window.shopify');
        console.error('[AppBridge] Ensure App Bridge script is in HTML head with data-api-key');
        reject(new Error('App Bridge not loaded - check if script is in HTML head'));
      } else {
        if (attempts % 10 === 0) {
          console.log(`[AppBridge] Still waiting... (${attempts * 100}ms)`);
        }
        setTimeout(checkShopify, 100);
      }
    };

    checkShopify();
  });
};

export const initializeAppBridge = async () => {
  if (appInstance) {
    return appInstance;
  }

  if (typeof window === 'undefined') {
    console.warn('Window not available - cannot initialize App Bridge');
    return null;
  }

  let { shop, host } = getEmbeddedParams();

  // Fallback to storage if not in URL
  if (!shop || !host) {
    shop = sessionStorage.getItem('shopify_shop');
    host = sessionStorage.getItem('shopify_host');
  }

  const normalizedShop = normalizeShopDomain(shop);

  if (!normalizedShop || !host) {
    console.warn('Missing shop or host parameter (and not in storage) - app may not be embedded. Skipping App Bridge initialization.');
    return null;
  }

  const apiKey = process.env.REACT_APP_SHOPIFY_API_KEY;
  console.log('🔍 DEBUG: Raw REACT_APP_SHOPIFY_API_KEY:', apiKey, 'Type:', typeof apiKey);

  if (!apiKey) {
    console.error('REACT_APP_SHOPIFY_API_KEY not configured; cannot initialize App Bridge.');
    return null;
  }

  // Wait for window.shopify if not already available
  if (!window.shopify) {
    try {
      await waitForAppBridge();
    } catch (error) {
      console.error('Failed to load App Bridge:', error);
      return null;
    }
  }

  try {
    // App Bridge CDN script auto-configures via data-api-key attribute
    // We just need to verify it's loaded and ready
    if (window.shopify) {
      console.log('[AppBridge] ✅ window.shopify available:', Object.keys(window.shopify));

      // Check if idToken is available (main method we need)
      if (typeof window.shopify.idToken === 'function') {
        console.log('[AppBridge] ✅ idToken method available - App Bridge ready!');
        appInstance = { version: 'cdn', ready: true };
        return appInstance;
      }

      // Fallback: try config() for older versions
      if (typeof window.shopify.config === 'function') {
        console.log('[AppBridge] Using config() method...');
        window.shopify.config({
          apiKey: apiKey,
          host: host
        });
        appInstance = { version: 'v4', configured: true };
        console.log('[AppBridge] ✅ Configured via config()');
        return appInstance;
      }

      // If window.shopify exists but no methods, it might still work
      console.log('[AppBridge] ⚠️ window.shopify exists but no idToken/config. Proceeding anyway.');
      appInstance = { version: 'unknown', ready: true };
      return appInstance;
    } else {
      console.error('[AppBridge] ❌ window.shopify not available');
      return null;
    }
  } catch (error) {
    console.error('[AppBridge] Failed to initialize:', error);
    return null;
  }
};

/**
 * Get session token for API requests with caching
 */
export const getShopifySessionToken = async () => {
  // Session tokens are only available in embedded apps
  if (!isEmbeddedApp()) {
    throw new Error('Session tokens only available in embedded mode');
  }

  // Check if we have a valid cached token
  if (sessionTokenCache && sessionTokenExpiry && Date.now() < sessionTokenExpiry) {
    return sessionTokenCache;
  }

  // Helper retry function
  const fetchTokenWithRetry = async (retries = 3, delay = 500) => {
    try {
      // Ensure App Bridge is initialized
      if (!appInstance && (!window.shopify || !window.shopify.config)) {
        appInstance = await initializeAppBridge();
      }

      if (window.shopify && typeof window.shopify.idToken === 'function') {
        return await window.shopify.idToken();
      } else if (appInstance && appInstance.idToken) {
        return await appInstance.idToken();
      } else if (window.shopify && window.shopify.app && window.shopify.app.idToken) {
        return await window.shopify.app.idToken();
      }

      throw new Error('No idToken method found');
    } catch (err) {
      if (retries > 0) {
        console.log(`[AppBridge] Token fetch failed, retrying in ${delay}ms... (${retries} left)`);
        await new Promise(r => setTimeout(r, delay));
        return fetchTokenWithRetry(retries - 1, delay * 1.5);
      }
      throw err;
    }
  };

  try {
    const token = await fetchTokenWithRetry();

    // Cache token for 50 seconds (tokens expire after 60 seconds)
    sessionTokenCache = token;
    sessionTokenExpiry = Date.now() + 50000;

    return token;
  } catch (error) {
    console.error('Failed to get session token:', error);
    // Clear cache on error
    sessionTokenCache = null;
    sessionTokenExpiry = null;
    throw error;
  }
};

/**
 * Check if app is embedded in Shopify admin
 */
/**
 * Check if app is embedded in Shopify admin
 * Persist the check to sessionStorage to handle navigation/refreshes where URL params might be lost
 */
export const isEmbeddedApp = () => {
  if (typeof window === 'undefined') return false;

  // 1. Check URL parameters
  const { shop, host } = getEmbeddedParams();
  if (shop && host) {
    try {
      sessionStorage.setItem('shopify_embedded', 'true');
      sessionStorage.setItem('shopify_shop', shop);
      sessionStorage.setItem('shopify_host', host);
    } catch (e) { /* ignore storage errors */ }
    return true;
  }

  // 2. Check if running in iframe
  try {
    if (window.self !== window.top) {
      return true;
    }
  } catch (e) {
    // Cross-origin access might fail if top is different domain, which implies iframe
    return true;
  }

  // 3. Check persistent storage
  try {
    return sessionStorage.getItem('shopify_embedded') === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Get shop domain from URL
 */
export const getShopDomain = () => {
  const { shop } = getEmbeddedParams();
  return normalizeShopDomain(shop);
};

/**
 * Enhanced fetch function that automatically includes session token for embedded apps
 */
const fetchWithSessionToken = async (url, options = {}) => {
  let headers = { ...options.headers };

  // If embedded, use session token instead of stored JWT
  if (isEmbeddedApp()) {
    try {
      const sessionToken = await getShopifySessionToken();
      headers['Authorization'] = `Bearer ${sessionToken}`;
      headers['X-Shopify-Session-Token'] = sessionToken;
    } catch (error) {
      console.error('Failed to get session token for request:', error);
      // Fall back to regular auth if session token fails
    }
  } else {
    // Use regular JWT token for non-embedded access
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
  }

  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  });
};

/**
 * Redirect to external URL - uses App Bridge redirect for embedded apps
 */
export const redirectToUrl = (url) => {
  // For OAuth URLs (Google, Shopify auth), always open in new window to avoid iframe restrictions
  if (url.includes('accounts.google.com') ||
    url.includes('accounts.shopify.com') ||
    url.includes('oauth') ||
    url.includes('auth')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  if (isEmbeddedApp() && appInstance) {
    try {
      // Use App Bridge redirect for embedded apps
      if (window.shopify && window.shopify.redirect) {
        window.shopify.redirect(url);
        return;
      } else if (appInstance.redirect) {
        appInstance.redirect(url);
        return;
      }
    } catch (error) {
      console.error('App Bridge redirect failed, falling back to window.location:', error);
    }
  }

  // Fallback to regular redirect for non-embedded apps or if App Bridge fails
  window.location.href = url;
};

const ShopifyAppBridgeUtils = {
  initializeAppBridge,
  getShopifySessionToken,
  isEmbeddedApp,
  getShopDomain,
  fetchWithSessionToken,
  redirectToUrl
};

export default ShopifyAppBridgeUtils;
